import { call, put, select, take } from 'redux-saga/effects';
import moment from 'moment';
import { chatMessage, chatConfirm, chatReset } from '../services/chatbotApi';
import { buildCampaignState } from '../services/buildCampaignState';
import ChatbotActions from '../redux/ChatbotRedux';
import DisplayActions, { DisplayTypes } from '../../../../redux/DisplayRedux';

/* ------------- Helpers ------------- */

const newSessionId = () => Math.floor(Math.random() * 1e9) + 1;

const extractError = resp =>
  (resp.data && resp.data.detail) || resp.problem || 'Error';

/* ------------- sendMessage ------------- */

export function* sendMessageSaga(action) {
  const { userId, text } = action;
  const sessionId = yield select(state => state.chatbot.sessionId);
  const display = yield select(state => state.display);
  const userAccount = yield select(state => state.userAccount);

  // Instead of a bare campaign_id, send the full campaign-state snapshot so the
  // microservice no longer needs the server-to-server GET /ai/campaign-state.
  // `selected_displays` travels inside the `campaign` object. The creation-case
  // invoice country lives in `userAccount`, so it is selected here too.
  const { campaign } = buildCampaignState(display, userAccount);

  const resp = yield call(chatMessage, {
    session_id: sessionId,
    user_id: userId,
    message: text,
    campaign,
  });

  if (resp.ok) {
    yield put(ChatbotActions.sendMessageSuccess(resp.data));
  } else {
    yield put(ChatbotActions.sendMessageFailure([extractError(resp)]));
  }
}

/* ------------- confirmProposal ------------- */

export function* confirmProposalSaga(action) {
  const { proposalAction, confirmed, checkedIds } = action;
  const { sessionId, currentProposalId, currentProposal } = yield select(
    state => state.chatbot
  );

  // The microservice only produces conversational feedback — applying the change
  // to the campaign is the frontend's job. We update the in-memory campaign
  // object through the DisplayRedux controllers (all synchronous reducers), so
  // both the editor UI and the next campaign-state snapshot reflect it. The
  // change persists to the backend when the user saves the campaign normally.
  let applied = {};
  if (confirmed) {
    const result = yield call(
      applyProposalToDsp,
      proposalAction,
      currentProposal,
      checkedIds
    );
    applied = result.applied;
  }

  // The snapshot is built AFTER applying the change, so a chained proposal
  // reasons with up-to-date data (e.g. sees a just-added display).
  const display = yield select(state => state.display);
  const userAccount = yield select(state => state.userAccount);
  const { campaign } = buildCampaignState(display, userAccount);

  const resp = yield call(chatConfirm, {
    session_id: sessionId,
    proposal_id: currentProposalId,
    action: proposalAction,
    confirmed,
    ...applied,
    user_id: userAccount.user && userAccount.user.id,
    campaign,
  });

  if (resp.ok) {
    yield put(ChatbotActions.confirmProposalSuccess(resp.data));
  } else {
    yield put(ChatbotActions.confirmProposalFailure([extractError(resp)]));
  }
}

function* applyProposalToDsp(proposalAction, proposal, checkedIds) {
  const payload = proposal && proposal.payload;

  switch (proposalAction) {
    case 'add_displays': {
      const idsToAdd = checkedIds && checkedIds.length
        ? checkedIds
        : (payload && payload.display_ids) || [];
      if (idsToAdd.length) {
        const display = yield select(state => state.display);
        const userAccount = yield select(state => state.userAccount);
        const editCampaignActive = !!display.editCampaignActive;
        yield put(DisplayActions.checkDisplaysByIdsRequest({
          display_ids: idsToAdd,
          date_from: moment(display.date_from || new Date()).format('YYYY-MM-DD'),
          date_to: moment(display.date_to || new Date()).format('YYYY-MM-DD'),
          smart_campaign_enabled: display.programmatic === 'programmatic' ? 1 : 0,
          currency: editCampaignActive ? display.campaignCurrency : userAccount.currency,
          invoice_issuing_country: editCampaignActive
            ? display.campaignInvoiceIssuingCountry
            : userAccount.invoiceIssuingCountry,
          client_id: display.client ? display.client.id : null,
          is_guaranteed: display.isGuaranteed ? 1 : 0,
          external_dsp_id: display.dsp ? display.dsp.id : null,
        }));
        // The cart is filled asynchronously by checkDisplaysByIdsRequest; wait
        // for it so the campaign snapshot sent to /chat/confirm includes them.
        yield take([
          DisplayTypes.CHECK_DISPLAYS_BY_IDS_SUCCESS,
          DisplayTypes.CHECK_DISPLAYS_BY_IDS_FAILURE,
        ]);
      }
      return { applied: { applied_display_ids: idsToAdd } };
    }

    case 'delete_displays': {
      const idsToRemove = checkedIds && checkedIds.length
        ? checkedIds
        : (payload && payload.display_ids) || [];
      for (const id of idsToRemove) {
        // removeDisplayOnCampaign clears the relations/listResolutionWithContent;
        // removeDisplayFromCart keeps `cart` in sync so the next campaign-state
        // snapshot (selected_displays) reflects the removal.
        yield put(DisplayActions.removeDisplayOnCampaign(id));
        yield put(DisplayActions.removeDisplayFromCart(id));
      }
      return { applied: { applied_display_ids: idsToRemove } };
    }

    case 'update_campaign_budget': {
      if (!payload) return { applied: {} };
      // setBudget updates the local campaign object: it writes amountBudget (the
      // user-entered budget) and realAmount, so both the editor UI and the next
      // campaign-state snapshot (buildCampaignState reads amountBudget) reflect it.
      yield put(DisplayActions.setBudget(payload.new_budget));
      return { applied: { applied_budget: payload.new_budget } };
    }

    case 'update_campaign_dates': {
      if (!payload) return { applied: {} };
      const dateFrom = payload.new_date_from || payload.current_date_from;
      const dateTo = payload.new_date_to || payload.current_date_to;
      yield put(DisplayActions.setDates(dateFrom, dateTo));
      return {
        applied: {
          applied_date_from: dateFrom,
          applied_date_to: dateTo,
        },
      };
    }

    case 'change_campaign_type': {
      if (!payload) return { applied: {} };

      const newType = payload.new_type;
      const isProgrammatic = newType === 'programmatic' || newType === 'programmatic_pg';
      yield put(DisplayActions.setProgrammatic(isProgrammatic ? 'programmatic' : 'traditional'));
      yield put(DisplayActions.setIsGuaranteed(newType === 'programmatic_pg'));
      // The type change always recalculates the budget.
      if (payload.new_budget != null) {
        yield put(DisplayActions.setBudget(payload.new_budget));
      }
      return { applied: { applied_campaign_type: newType } };
    }

    case 'change_client': {
      if (!payload) return { applied: {} };
      // setClient writes display.client (buildCampaignState reads client.id) and
      // also resets brand/brands, mirroring the editor's behavior (brands are
      // client-scoped). The contract only carries id + name.
      yield put(DisplayActions.setClient({ id: payload.client_id, name: payload.client_name }));
      return { applied: {} };
    }

    case 'change_brand': {
      if (!payload) return { applied: {} };
      // setBrand writes display.brand (buildCampaignState reads brand.id).
      yield put(DisplayActions.setBrand({ id: payload.brand_id, name: payload.brand_name }));
      return { applied: {} };
    }

    case 'change_external_dsp': {
      if (!payload) return { applied: {} };
      // setDsp writes display.dsp (buildCampaignState reads dsp.id).
      yield put(DisplayActions.setDsp({ id: payload.dsp_id, name: payload.external_dsp_name }));
      return { applied: {} };
    }

    default:
      return { applied: {} };
  }
}

/* ------------- resetSession ------------- */

export function* resetSessionSaga() {
  const sessionId = yield select(state => state.chatbot.sessionId);
  const resp = yield call(chatReset, { session_id: sessionId });

  if (resp.ok) {
    yield put(ChatbotActions.resetSessionSuccess(newSessionId()));
  } else {
    yield put(ChatbotActions.resetSessionFailure([extractError(resp)]));
  }
}
